import { computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { useStore, useGetters } from 'vuex-composition-helpers'
import { useFormatting } from './useFormatting'
import { notEmpty } from '../utils/notEmpty'
import { getIconForChange } from '../utils/getIconForChange'
import { eventIsUnapproval } from '../utils/eventIsUnapproval'
import JobHistory from '@/models/job-history'
import { changeTypes } from '@/components/tickets/history/composables/ChangeTypes'
import { CustomFieldRecord } from '@/models/types/custom-field'
import JobChange from '@/models/job_change'
import { useCustomFieldPathParser } from './useCustomFieldPathParser'

interface ArrayPathInfo {
  basePath: string
  index: number
  hasTrailingSegments: boolean
}

interface ArraySummaryStats {
  addedIndexes: Set<number>
  removedIndexes: Set<number>
  updatedIndexes: Set<number>
}

export function useFormattedChanges(props: {
  date: string | number
  changes: JobHistory[]
  jobStart?: object | null
  jobEnd?: object | null
  jobHubId: string
  customFieldDefinitions?: CustomFieldRecord
}) {
  const { getPrincipalBySid } = useGetters('users', ['getPrincipalBySid'])

  const getUserName = (userSid: string) => {
    const name = getPrincipalBySid.value(userSid)?.name
    return name ? `by ${name}` : ''
  }

  const { isHostOrganizationUser, getOrganizationId: getUserOrgId } = useGetters('permissions', [
    'isHostOrganizationUser',
    'getOrganizationId',
  ])
  const store = useStore()

  const getBusinessUnitById = computed(() => store.getters['businessUnits/getBusinessUnitById'])

  const getOrganizationName = (id: string) => {
    return getBusinessUnitById.value(id)?.name ?? 'Unknown company'
  }

  const scadaUnits = computed<string>(() => store.state.organizationConfig.scadaUnits)

  const {
    formatPlannedTimeChange,
    formatActiveTimeChange,
    formatImportantField,
    formatSecondaryFieldName,
    formatSecondaryFieldsDisplay,
    formatType,
  } = useFormatting(props)

  const { getCustomFieldLabel, parseCustomDataPath } = useCustomFieldPathParser()

  const getArrayPathInfo = (columnName: string): ArrayPathInfo | null => {
    if (!columnName.startsWith('custom_data.')) {
      return null
    }
    const segments = parseCustomDataPath(columnName)
    const indexPos = segments.findIndex((segment) => typeof segment === 'number')
    if (indexPos === -1) {
      return null
    }
    const index = segments[indexPos] as number
    const baseSegments = segments.slice(0, indexPos)
    if (baseSegments.length === 0) {
      return null
    }
    const basePath = `custom_data.${baseSegments.join('.')}`
    const hasTrailingSegments = indexPos < segments.length - 1
    return { basePath, index, hasTrailingSegments }
  }

  const formatArraySummaryChange = (
    basePath: string,
    addedCount: number,
    removedCount: number,
    updatedCount: number,
  ) => {
    if (!addedCount && !removedCount && !updatedCount) {
      return null
    }
    const parts: string[] = []
    if (addedCount) {
      parts.push(`${addedCount} item${addedCount === 1 ? '' : 's'} added`)
    }
    if (removedCount) {
      parts.push(`${removedCount} item${removedCount === 1 ? '' : 's'} removed`)
    }
    if (updatedCount) {
      parts.push(`${updatedCount} item${updatedCount === 1 ? '' : 's'} updated`)
    }

    return {
      strikeLeft: false,
      columnName: basePath,
      originalValue: '',
      newValue: parts.join(', '),
      prefix: getCustomFieldLabel(basePath, props.customFieldDefinitions),
      isArraySummary: true,
    }
  }

  const expandObjectChange = (field: JobChange): JobChange[] => {
    // Only expand if:
    // 1. It's a custom_data field
    // 2. newValue is a plain object (not array, not null)
    // 3. originalValue is null/undefined (new object being added)
    if (
      !field.columnName.startsWith('custom_data.') ||
      (field.originalValue !== null && field.originalValue !== undefined) ||
      field.newValue === null ||
      field.newValue === undefined ||
      Array.isArray(field.newValue) ||
      typeof field.newValue !== 'object' ||
      field.newValue.constructor !== Object
    ) {
      return [field]
    }

    const expandedFields: JobChange[] = []
    const basePath = field.columnName

    // Recursively expand object properties
    const expandObject = (obj: Record<string, unknown>, pathPrefix: string): void => {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = pathPrefix ? `${pathPrefix}.${key}` : key

        // Skip null/undefined values
        if (value === null || value === undefined) {
          continue
        }

        // If value is a plain object (not array), recurse
        if (typeof value === 'object' && !Array.isArray(value) && value.constructor === Object) {
          expandObject(value as Record<string, unknown>, fullPath)
        } else {
          // Create a new field change for this property.
          // Important: expandObjectChange is only used when the parent field's
          // originalValue is null/undefined (see guard above), meaning we're
          // representing a newly added object. Therefore, expanded fields
          // intentionally use original_value: null to indicate "added" semantics.
          const expandedField = new JobChange({
            column_name: `custom_data.${fullPath}`,
            original_value: null,
            new_value: value,
            is_important: field.isImportant,
          })
          expandedFields.push(expandedField)
        }
      }
    }

    // Extract the path after "custom_data." to use as prefix
    const pathAfterPrefix = basePath.substring('custom_data.'.length)
    expandObject(field.newValue as Record<string, unknown>, pathAfterPrefix)

    // If we didn't expand anything (empty object), return the original field
    return expandedFields.length > 0 ? expandedFields : [field]
  }

  const formattedDate = computed(() => {
    const today = moment().format('MMM DD, YYYY')
    const yesterday = moment().subtract(1, 'day').format('MMM DD, YYYY')
    if (props.date === yesterday) {
      return `YESTERDAY · ${props.date}`
    } else if (props.date === today) {
      return `TODAY · ${props.date}`
    } else {
      return props.date
    }
  })

  const formattedChanges = computed(() => {
    const datetimeMinValue = '0001-01-01T00:00:00'

    return props.changes
      .map((change) => {
        // Extract time changes
        const startTimeChange = change.fieldsChanged.find(
          (field) => field.columnName === 'start_time' && field.originalValue !== datetimeMinValue,
        )
        const endTimeChange = change.fieldsChanged.find(
          (field) => field.columnName === 'end_time' && field.originalValue !== datetimeMinValue,
        )
        const activeTimeChange = change.fieldsChanged.find(
          (field) =>
            field.columnName === 'active_time' &&
            field.originalValue !== datetimeMinValue &&
            field.originalValue !== null,
        )
        const completedTimeChange = change.fieldsChanged.find(
          (field) =>
            field.columnName === 'completed_time' &&
            field.originalValue !== datetimeMinValue &&
            field.originalValue !== null,
        )

        const arraySummaryStats = new Map<string, ArraySummaryStats>()

        change.fieldsChanged.forEach((field) => {
          const pathInfo = getArrayPathInfo(field.columnName)
          if (!pathInfo) {
            return
          }
          const summary = arraySummaryStats.get(pathInfo.basePath) ?? {
            addedIndexes: new Set<number>(),
            removedIndexes: new Set<number>(),
            updatedIndexes: new Set<number>(),
          }
          arraySummaryStats.set(pathInfo.basePath, summary)

          const hasOriginal = field.originalValue !== null && field.originalValue !== undefined
          const hasNew = field.newValue !== null && field.newValue !== undefined

          if (!pathInfo.hasTrailingSegments) {
            if (hasNew && !hasOriginal) {
              summary.addedIndexes.add(pathInfo.index)
              summary.removedIndexes.delete(pathInfo.index)
              summary.updatedIndexes.delete(pathInfo.index)
            } else if (hasOriginal && !hasNew) {
              summary.removedIndexes.add(pathInfo.index)
              summary.addedIndexes.delete(pathInfo.index)
              summary.updatedIndexes.delete(pathInfo.index)
            } else if (hasOriginal && hasNew) {
              summary.updatedIndexes.add(pathInfo.index)
              summary.addedIndexes.delete(pathInfo.index)
              summary.removedIndexes.delete(pathInfo.index)
            }
            return
          }

          if (
            !summary.addedIndexes.has(pathInfo.index) &&
            !summary.removedIndexes.has(pathInfo.index)
          ) {
            summary.updatedIndexes.add(pathInfo.index)
          }
        })

        Array.from(arraySummaryStats.entries()).forEach(([basePath, summary]) => {
          if (
            summary.addedIndexes.size === 0 &&
            summary.removedIndexes.size === 0 &&
            summary.updatedIndexes.size === 0
          ) {
            arraySummaryStats.delete(basePath)
          }
        })

        // Expand object changes into individual property changes
        // This handles cases like custom_data.container[1] with {quantity: 1, type: "Basket"}
        // by expanding them into custom_data.container[1].quantity, custom_data.container[1].type, etc.
        const expandedFieldsChanged = change.fieldsChanged.flatMap((field) =>
          expandObjectChange(field),
        )

        // Check for granular custom_data changes BEFORE filtering array changes
        // This ensures we detect granular fields even if they get filtered out for array summaries
        const hasGranularCustomDataChanges = expandedFieldsChanged.some(
          (field) =>
            field.columnName.startsWith('custom_data.') && field.columnName !== 'custom_data',
        )

        const filteredFieldsChanged = expandedFieldsChanged.filter((field) => {
          const pathInfo = getArrayPathInfo(field.columnName)
          if (!pathInfo) {
            return true
          }
          const summary = arraySummaryStats.get(pathInfo.basePath)
          if (!summary) {
            return true
          }
          if (
            summary.addedIndexes.has(pathInfo.index) ||
            summary.removedIndexes.has(pathInfo.index) ||
            summary.updatedIndexes.has(pathInfo.index)
          ) {
            return false
          }
          return true
        })

        // Format important fields, including custom_data.* fields
        const arraySummaryChanges = Array.from(arraySummaryStats.entries())
          .map(([basePath, stats]) =>
            formatArraySummaryChange(
              basePath,
              stats.addedIndexes.size,
              stats.removedIndexes.size,
              stats.updatedIndexes.size,
            ),
          )
          .filter(notEmpty)

        const importantFields = [
          ...arraySummaryChanges,
          ...filteredFieldsChanged
            .filter((field) => field.isImportant)
            .map((field) => formatImportantField(field, change)),
          // Add non-important custom_data.* fields as important fields
          ...filteredFieldsChanged
            .filter(
              (field) =>
                !field.isImportant &&
                field.columnName.startsWith('custom_data.') &&
                field.columnName !== 'custom_data',
            )
            .map((field) => formatImportantField(field, change)),
          change.type === changeTypes.REOPEN
            ? null
            : formatPlannedTimeChange(props, startTimeChange, endTimeChange),
          change.type === changeTypes.REOPEN
            ? null
            : formatActiveTimeChange(activeTimeChange, completedTimeChange),
        ].filter(notEmpty)

        // Format secondary fields, excluding generic "custom_data" when granular changes exist
        const secondaryFieldsChanged = filteredFieldsChanged
          .filter((field) => {
            if (!field.isImportant) {
              // Exclude generic "custom_data" field if we have granular custom_data.* changes
              if (hasGranularCustomDataChanges && field.columnName === 'custom_data') {
                return false
              }
              // Exclude granular custom_data.* fields (they're now in importantFields)
              if (field.columnName.startsWith('custom_data.')) {
                return false
              }
              return true
            }
            return false
          })
          .map((field) => formatSecondaryFieldName(field.columnName))
          .filter(notEmpty)

        let secondaryFields = formatSecondaryFieldsDisplay(secondaryFieldsChanged)

        // Handle unapproval events
        if (eventIsUnapproval(change)) {
          change.type = changeTypes.UNAPPROVED
        }

        // Handle availability events
        if (
          [changeTypes.AVAILABILITY_ACCEPTED, changeTypes.AVAILABILITY_DECLINED].includes(
            change.type,
          )
        ) {
          const orgId = getPrincipalBySid.value(change.modifiedBy)?.organizationId

          if (!orgId && !isHostOrganizationUser.value) {
            return null
          }

          if (!isHostOrganizationUser.value && orgId !== getUserOrgId.value) {
            return null
          }

          if (orgId) {
            secondaryFields = `On behalf of ${getOrganizationName(orgId)}`
          }
        }

        if (change.type === 'trigger_threshold_resolved') {
          secondaryFields = `Level dropped by ${Math.round(
            change.fieldsChanged[0].originalValue.max - change.fieldsChanged[0].originalValue.min,
          )} ${scadaUnits.value}`
        }

        if (change.type === 'item_received_at_location') {
          secondaryFields = `Received ${Math.round(
            change.fieldsChanged[0].originalValue.quantity,
          )} ${scadaUnits.value}`
        }

        // Adjust change type for modified events
        let changeType = change.type
        if (changeType === changeTypes.MODIFIED) {
          const pendingApprovalFieldChange = change.fieldsChanged.find(
            (f) => f.columnName === 'pending_approval',
          )
          if (pendingApprovalFieldChange) {
            if (pendingApprovalFieldChange.originalValue && !pendingApprovalFieldChange.newValue) {
              changeType = 'approval_request_cancelled'
            }
          }
        }

        return {
          importantFields,
          secondaryFields,
          iconInfo: getIconForChange(changeType),
          modified: change.modified,
          modifiedDisplay: change.modified.format('HH:mm'),
          modifiedBy: getUserName(change.modifiedBy),
          type: formatType(changeType, change.modifiedBy, change.fieldsChanged),
          uuid: uuidv4(),
        }
      })
      .filter(notEmpty)
  })

  return {
    formattedDate,
    formattedChanges,
  }
}
